const DAYS_PER_MONTH = 30;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

const calculateTimeDifferenceMs = ({
  date,
  shouldSetToStartOfDay = false,
}: {
  date: string;
  shouldSetToStartOfDay?: boolean;
}): number => {
  const now = new Date();
  const then = new Date(date);
  if (shouldSetToStartOfDay) {
    now.setHours(0, 0, 0, 0);
    then.setHours(0, 0, 0, 0);
  }
  return now.getTime() - then.getTime();
};

export const dates = {
  /**
   * Calculates the number of hours ago from a given date.
   */
  calculateHoursAgo: (date: string): number => {
    const diff = calculateTimeDifferenceMs({ date });
    return Math.floor(diff / MS_PER_HOUR);
  },

  /**
   * Calculates the number of days ago from a given date, setting the date and current date to the start of the day.
   */
  calculateDaysAgo: (date: string): number => {
    const diff = calculateTimeDifferenceMs({
      date,
      shouldSetToStartOfDay: true,
    });
    return Math.floor(diff / MS_PER_DAY);
  },

  prepareDateDisplayForCertificationOrEducationOrWorkExperience: ({
    startMonth,
    startYear,
    endMonth,
    endYear,
    current,
  }: {
    startMonth: string | null;
    startYear: string | null;
    endMonth: string | null;
    endYear: string | null;
    current: boolean | null;
  }): string => {
    let start = "";
    if (startMonth === "Unspecified" && startYear === "Unspecified") {
      start = "Unspecified";
    } else if (startMonth === null && startYear !== null) {
      start = startYear;
    } else if (startMonth !== null && startYear !== null) {
      start = `${startMonth} ${startYear}`;
    }

    let end = "";
    if (endMonth === "Unspecified" && endYear === "Unspecified") {
      end = "Unspecified";
    } else if (endMonth === null && endYear !== null) {
      end = endYear;
    } else if (endMonth !== null && endYear !== null) {
      end = `${endMonth} ${endYear}`;
    } else if (current) {
      end = "Present";
    }

    if (start === "Unspecified" && end === "Unspecified") {
      return "Unspecified";
    } else if (!start && end) {
      return end;
    } else if (start && !end) {
      return start;
    } else if (start && end) {
      return `${start} - ${end}`;
    }
    return "";
  },

  /**
   * Converts a date into a human-readable relative time string for jobs.
   */
  timeAgoForJob: (date: string): string => {
    const daysAgo = dates.calculateDaysAgo(date);

    if (daysAgo === 0) {
      return "today";
    }
    if (daysAgo === 1) {
      return "yesterday";
    }
    if (daysAgo < DAYS_PER_MONTH) {
      return `${daysAgo.toString()} days ago`;
    }

    const monthsAgo = Math.floor(daysAgo / DAYS_PER_MONTH);
    return monthsAgo === 1
      ? "1 month ago"
      : `${monthsAgo.toString()} months ago`;
  },

  /**
   * Converts a date into a human-readable relative time string for pipeline activity.
   */
  timeAgoForPipeline: (date: string): string => {
    const hoursAgo = dates.calculateHoursAgo(date);
    const daysAgo = dates.calculateDaysAgo(date);

    if (hoursAgo < 1) {
      return "< 1 hour ago";
    }
    if (hoursAgo === 1) {
      return "1 hour ago";
    }
    if (hoursAgo < 24) {
      return `${hoursAgo.toString()} hours ago`;
    }
    if (daysAgo === 1) {
      return "1 day ago";
    }
    if (daysAgo < DAYS_PER_MONTH * 3) {
      return `${daysAgo.toString()} days ago`;
    }
    return "> 3 months ago";
  },

  /**
   * Converts a date into a human-readable relative time string for pipeline Kanban cards specifically.
   */
  timeAgoForPipelineKanbanCard: (date: string): string => {
    const hoursAgo = dates.calculateHoursAgo(date);
    const daysAgo = dates.calculateDaysAgo(date);

    if (hoursAgo < 1) {
      return "< 1h";
    }
    if (hoursAgo === 1) {
      return "1h";
    }
    if (hoursAgo < 24) {
      return `${hoursAgo.toString()}h`;
    }
    if (daysAgo === 1) {
      return "1d";
    }
    if (daysAgo < DAYS_PER_MONTH * 3) {
      return `${daysAgo.toString()}d`;
    }
    return "> 3m";
  },
};
